import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { User } from '@evidentid/tprm-portal-lib/models/user-management/User.model';
import { UserManagementApiClient } from '@evidentid/user-management-api-client';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { UserInput, UserManagementConfig } from './types';

export interface UserManagementRequirements {
    userManagement: UserManagementApiClient;
    gatewayClient: GatewayApiClient;
    options: {
        userManagementConfig: UserManagementConfig;
    };
}

export interface BaseUserStatus {
    status: OperationStatus;
    error: any;
}

export interface UserManagementUsersList extends BaseUserStatus {
    nextToken: string | null;
    users: User[];
    canLoadMore: boolean;
}

export interface UserManagementState {
    usersList: Record<string, UserManagementUsersList>;
    inviteUserStatus: Record<string, BaseUserStatus>;
    updateUserStatus: Record<string, Record<string, BaseUserStatus>>;
    deleteUserStatus: Record<string, Record<string, BaseUserStatus>>;
    resendInviteStatus: Record<string, Record<string, BaseUserStatus>>;
}

export const emptyUsersListStatus: UserManagementUsersList = {
    status: OperationStatus.uninitialized,
    nextToken: null,
    users: [],
    canLoadMore: true,
    error: null,
};

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): UserManagementRequirements {
    return store.$app;
}

export const useUserManagementStore = defineStore('userManagement', {
    state: (): UserManagementState => ({
        usersList: {},
        inviteUserStatus: {},
        updateUserStatus: {},
        deleteUserStatus: {},
        resendInviteStatus: {},
    }),
    actions: {
        startLoadingMoreUsers(payload: { rpName: string }) {
            this.usersList = {
                ...this.usersList,
                [payload.rpName]: {
                    ...emptyUsersListStatus,
                    ...this.usersList[payload.rpName],
                    status: OperationStatus.loading,
                },
            };
        },
        failLoadingMoreUsers(payload: { rpName: string, error: any }) {
            this.usersList = {
                ...this.usersList,
                [payload.rpName]: {
                    ...emptyUsersListStatus,
                    ...this.usersList[payload.rpName],
                    status: OperationStatus.error,
                    error: payload.error,
                },
            };
        },
        finishLoadingMoreUsers(payload: { rpName: string, users: User[], nextToken: string | null }) {
            this.usersList = {
                ...this.usersList,
                [payload.rpName]: {
                    ...emptyUsersListStatus,
                    ...this.usersList[payload.rpName],
                    status: OperationStatus.success,
                    nextToken: payload.nextToken,
                    canLoadMore: Boolean(payload.nextToken),
                    users: [
                        ...(this.usersList[payload.rpName]?.users || []),
                        ...payload.users,
                    ],
                },
            };
        },
        resetUsersList(payload: { rpName: string }) {
            this.usersList = omit(this.usersList, [ payload.rpName ]);
        },
        startInvitingUser(payload: { rpName: string }) {
            this.inviteUserStatus = {
                ...this.inviteUserStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    error: null,
                },
            };
        },
        failInvitingUser(payload: { rpName: string, error: any }) {
            this.inviteUserStatus = {
                ...this.inviteUserStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    error: payload.error,
                },
            };
        },
        finishInvitingUser(payload: { rpName: string }) {
            this.inviteUserStatus = {
                ...this.inviteUserStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    error: null,
                },
            };
        },
        startUpdatingUser(payload: { rpName: string, id: string }) {
            this.updateUserStatus = {
                ...this.updateUserStatus,
                [payload.rpName]: {
                    ...(this.updateUserStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.loading,
                        error: null,
                    },
                },
            };
        },
        failUpdatingUser(payload: { rpName: string, id: string, error: any }) {
            this.updateUserStatus = {
                ...this.updateUserStatus,
                [payload.rpName]: {
                    ...(this.updateUserStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.error,
                        error: payload.error,
                    },
                },
            };
        },
        finishUpdatingUser(payload: { rpName: string, id: string }) {
            this.updateUserStatus = {
                ...this.updateUserStatus,
                [payload.rpName]: {
                    ...(this.updateUserStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.success,
                        error: null,
                    },
                },
            };
        },
        startDeletingUser(payload: { rpName: string, id: string }) {
            this.deleteUserStatus = {
                ...this.deleteUserStatus,
                [payload.rpName]: {
                    ...(this.deleteUserStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.loading,
                        error: null,
                    },
                },
            };
        },
        failDeletingUser(payload: { rpName: string, id: string, error: any }) {
            this.deleteUserStatus = {
                ...this.deleteUserStatus,
                [payload.rpName]: {
                    ...(this.deleteUserStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.error,
                        error: payload.error,
                    },
                },
            };
        },
        finishDeletingUser(payload: { rpName: string, id: string }) {
            this.deleteUserStatus = {
                ...this.deleteUserStatus,
                [payload.rpName]: {
                    ...(this.deleteUserStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.success,
                        error: null,
                    },
                },
            };
        },
        startResendingInvite(payload: { rpName: string, id: string }) {
            this.resendInviteStatus = {
                ...this.resendInviteStatus,
                [payload.rpName]: {
                    ...(this.resendInviteStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.loading,
                        error: null,
                    },
                },
            };
        },
        failResendingInvite(payload: { rpName: string, id: string, error: any }) {
            this.resendInviteStatus = {
                ...this.resendInviteStatus,
                [payload.rpName]: {
                    ...(this.resendInviteStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.error,
                        error: payload.error,
                    },
                },
            };
        },
        finishResendingInvite(payload: { rpName: string, id: string }) {
            this.resendInviteStatus = {
                ...this.resendInviteStatus,
                [payload.rpName]: {
                    ...(this.resendInviteStatus[payload.rpName] || {}),
                    [payload.id]: {
                        status: OperationStatus.success,
                        error: null,
                    },
                },
            };
        },

        async loadMoreUsers({ rpName }: { rpName: string }) {
        // Obtain current information
            const currentState = this.usersList[rpName] || emptyUsersListStatus;

            // Ignore when it's already loading more users, or can't load more
            if (currentState.status === OperationStatus.loading || !currentState.canLoadMore) {
                return;
            }

            try {
            // Load more users
                this.startLoadingMoreUsers({ rpName });
                const prevState = this.usersList[rpName] || emptyUsersListStatus;

                const { results, next } = await appOf(this).gatewayClient.tprmUi().getUsers(rpName, {
                    page: currentState.nextToken,
                    limit: appOf(this).options.userManagementConfig.usersPerPage || 10,
                });

                // Ensure there is no race condition
                const nextState = this.usersList[rpName] || emptyUsersListStatus;
                if (prevState !== nextState) {
                    return;
                }

                // Push new data
                this.finishLoadingMoreUsers({
                    rpName,
                    users: results,
                    nextToken: next,
                });
            } catch (error) {
                this.failLoadingMoreUsers({ rpName, error });
            }
        },

        async inviteUser(payload: {
            rpName: string;
            rpDisplayName?: string | null;
            input: UserInput;
        }) {
            const { rpName, rpDisplayName, input } = payload;
            const status = this.inviteUserStatus[rpName]?.status || OperationStatus.uninitialized;

            // Ignore when some invitation to this RP is already in progress
            if (status === OperationStatus.loading) {
                return;
            }

            // Invite user
            try {
                this.startInvitingUser({ rpName });

                const loginUrl = Object.assign(new URL(location.href), { hash: '#/auth' }).toString();
                const user = {
                    name: input.name,
                    email: input.email,
                    role: input.role,
                };
                await appOf(this).gatewayClient.tprmUi().createUser(rpName, {
                    user,
                    loginUrl,
                    displayName: rpDisplayName || rpName,
                });

                this.finishInvitingUser({ rpName });
            } catch (error) {
                this.failInvitingUser({ rpName, error });
            }
        },

        async updateUser(payload: {
            id: string;
            rpName: string;
            input: UserInput;
        }) {
            const { rpName, id, input } = payload;
            const status = this.inviteUserStatus[rpName]?.status || OperationStatus.uninitialized;

            // Ignore when some update to this user is already in progress
            if (status === OperationStatus.loading) {
                return;
            }

            // Invite user
            try {
                this.startUpdatingUser({ rpName, id });

                const user = {
                    name: input.name,
                    email: input.email,
                    role: input.role,
                };
                await appOf(this).gatewayClient.tprmUi().updateUser(rpName, id, user);

                this.finishUpdatingUser({ rpName, id });
            } catch (error) {
                this.failUpdatingUser({ rpName, id, error });
            }
        },

        async deleteUser(payload: {
            id: string;
            rpName: string;
        }) {
            const { rpName, id } = payload;
            const status = this.inviteUserStatus[rpName]?.status || OperationStatus.uninitialized;

            // Ignore when some deletion of this user is already in progress
            if (status === OperationStatus.loading) {
                return;
            }

            // Invite user
            try {
                this.startDeletingUser({ rpName, id });
                await appOf(this).gatewayClient.tprmUi().deleteUser(rpName, id);
                this.finishDeletingUser({ rpName, id });
            } catch (error) {
                this.failDeletingUser({ rpName, id, error });
            }
        },

        async resendInvite(payload: {
            id: string;
            rpName: string;
        }) {
            const { rpName, id } = payload;
            const status = this.inviteUserStatus[rpName]?.status || OperationStatus.uninitialized;

            // Ignore when some deletion of this user is already in progress
            if (status === OperationStatus.loading) {
                return;
            }

            // Invite user
            try {
                this.startResendingInvite({ rpName, id });
                const loginUrl = Object.assign(new URL(location.href), { hash: '#!/auth' }).toString();
                const isSuccess = await appOf(this).gatewayClient.tprmUi().resendInvite(rpName, id, loginUrl);
                if (!isSuccess) {
                    throw new Error('It was not possible to re-send invite to this user.');
                }

                this.finishResendingInvite({ rpName, id });
            } catch (error) {
                this.failResendingInvite({ rpName, id, error });
            }
        },
    },
});
